import { useMemo } from 'react'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { AutopilotPlan, StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { requiredExtraAccounts } from 'components/Settings/TieredBilling/utils'

export type AddOnDisplayItem = {
  key: PaymentPlanKey.BlazeAutopilotExtraAccounts | PaymentPlanKey.BlazeAutopilotWhiteLabel
  name: string
  monthlyPriceTotal: number
  price: number
  tooltip: string | null
  monthlyPriceForSingleAccount: number
}

/**
 * Add-on line items for the checkout summary.
 *
 * Pass `targetPlan` when summarizing a plan the workspace is moving to: extra accounts are only
 * charged for what that plan's allowance does not already cover, so the line disappears when the
 * new plan absorbs them (BLZ-16162). Omit it to summarize the current subscription as billed.
 */
export const useAddOnDisplayItems = (targetPlan?: AutopilotPlan | null): AddOnDisplayItem[] => {
  const { currentSubscription, autopilotAddOns = [] } = useRootStore().tierUsageStore

  return useMemo(() => {
    if (!currentSubscription?.subscription_items) return []

    const items: AddOnDisplayItem[] = []

    const extraAccountItem = currentSubscription.subscription_items.find(
      (item: any) => item.plan_key === PaymentPlanKey.BlazeAutopilotExtraAccounts,
    )
    const totalAccounts = requiredExtraAccounts(currentSubscription, targetPlan)
    if (totalAccounts > 0) {
      // Prefer the price already on the subscription; fall back to the catalog so a plan change that
      // newly requires extra accounts can still be priced.
      const subscribedPrice = currentSubscription.prices?.find((p: StripePrice) => p.id === extraAccountItem?.price_id)
      const catalogPrice = autopilotAddOns
        .find((addOn) => addOn.key === PaymentPlanKey.BlazeAutopilotExtraAccounts)
        ?.prices?.find((p) => !!p.recurring)
      const price = subscribedPrice?.recurring ? subscribedPrice : catalogPrice
      // A one-time price has no interval to divide by, so it can't be shown as a monthly add-on.
      const monthlyPrice = price?.recurring ? price.unit_amount / price.recurring.interval_count / 100 : 0
      if (monthlyPrice > 0) {
        items.push({
          key: PaymentPlanKey.BlazeAutopilotExtraAccounts,
          name: `${totalAccounts} extra account${totalAccounts === 1 ? '' : 's'}`,
          monthlyPriceTotal: monthlyPrice * totalAccounts,
          price: price ? (price.unit_amount / 100) * totalAccounts : 0,
          tooltip: 'Additional posting accounts to increase your online presence.',
          monthlyPriceForSingleAccount: monthlyPrice,
        })
      }
    }

    const whiteLabelItem = currentSubscription.subscription_items.find(
      (item: any) => item.plan_key === PaymentPlanKey.BlazeAutopilotWhiteLabel,
    )
    if (whiteLabelItem) {
      const price = currentSubscription.prices?.find((p: StripePrice) => p.id === whiteLabelItem.price_id)
      const monthlyPriceTotal = price?.recurring ? price.unit_amount / price.recurring.interval_count / 100 : 0
      if (monthlyPriceTotal > 0) {
        items.push({
          key: PaymentPlanKey.BlazeAutopilotWhiteLabel,
          name: 'White Label',
          monthlyPriceTotal,
          price: price ? price.unit_amount / 100 : 0,
          tooltip: 'Change the visual identity of your workspace with your logo and colors.',
          monthlyPriceForSingleAccount: monthlyPriceTotal,
        })
      }
    }

    return items
  }, [currentSubscription, autopilotAddOns, targetPlan])
}
